import { forwardRef, type HTMLAttributes, type JSX, type ReactNode } from 'react';
import clsx from 'clsx';

export interface FormMessageProps extends HTMLAttributes<HTMLDivElement> {
  tone?: 'neutral' | 'success' | 'error';
  children: ReactNode;
  role?: 'status' | 'alert';
  ariaLive?: 'polite' | 'assertive';
}

export const FormMessage = forwardRef<HTMLDivElement, FormMessageProps>(function FormMessage(
  { tone = 'neutral', children, role = 'status', ariaLive, ...rest },
  ref,
): JSX.Element {
  const toneClasses = {
    neutral: 'border-brand-primary/20 bg-brand-primary/5 text-text-primary',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    error: 'border-red-200 bg-red-50 text-red-700',
  } as const;

  const liveRegion = ariaLive ?? (role === 'alert' ? 'assertive' : 'polite');

  return (
    <div
      ref={ref}
      className={clsx('rounded-lg border px-3 py-2 text-sm', toneClasses[tone])}
      role={role}
      aria-live={liveRegion}
      {...rest}
    >
      {children}
    </div>
  );
});
